'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Breadcrumbs as MantineBreadcrumbs, Anchor, Text, ThemeIcon, Group } from '@mantine/core';
import { navItems, getNavItemsWithT } from './layout/navItems';
import { useTranslation } from 'react-i18next';

function capitalizeLabel(s: string) {
  if (!s) {
    return s;
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Breadcrumbs implemented with Mantine components and localized labels.
 */
export default function Breadcrumbs() {
  const pathname = usePathname() || '/';
  const { t, ready } = useTranslation();

  // Split and skip empty segments
  const segments = pathname.split('/').filter(Boolean);

  // Use localized nav items only when translations are ready on the client.
  const localizedNav = React.useMemo(() => (ready ? getNavItemsWithT(t) : navItems), [t, ready]);
  const parts = segments.map((seg, idx) => {
    const href = `/${segments.slice(0, idx + 1).join('/')}`;
    // Try to find the nav item in localized nav or fallback navItems.
    // If not found at top level, search children of grouped items.
    let item = localizedNav.find((n) => n.href === href) || navItems.find((n) => n.href === href);
    if (!item) {
      // search children
      for (const parent of localizedNav.concat(navItems)) {
        const children = parent.children;
        if (children) {
          const found = children.find((c) => c.href === href);
          if (found) {
            item = found;
            break;
          }
        }
      }
    }
    const rawLabel = item ? item.label : decodeURIComponent(seg).replace(/-/g, ' ');
    let label = typeof rawLabel === 'string' ? rawLabel : String(rawLabel);

    // Ensure label is properly capitalized and localized where possible
    if (label.toLowerCase() === 'inicio') {
      label = ready ? t('inicio') : 'Inicio';
    } else {
      label = capitalizeLabel(label);
    }
    return { href, label };
  });

  // Home (localized when ready) + parts
  const homeLabel = ready ? t('inicio') : 'Inicio';
  const crumbs = [{ href: '/', label: homeLabel }, ...parts];

  return (
    <MantineBreadcrumbs
      separator={
        <Text c="dimmed" opacity={0.6} px={4}>
          /
        </Text>
      }
      fz="sm"
      c="dimmed"
      my="sm"
    >
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        // Try to get an icon for this crumb
        const navItem = navItems.find((n) => n.href === c.href);
        const Icon = navItem?.Icon;
        return isLast ? (
          <Group gap={6} align="center" key={`${i}-${c.href}`}>
            {Icon ? (
              <ThemeIcon size={18} radius="sm" variant="light">
                <Icon size={12} />
              </ThemeIcon>
            ) : null}
            <Text fw={600} c="dimmed">
              {c.label}
            </Text>
          </Group>
        ) : (
          <Anchor key={`${i}-${c.href}`} component={Link} href={c.href} variant="link">
            <Group gap={6} align="center">
              {Icon ? (
                <ThemeIcon size={18} radius="sm" variant="light">
                  <Icon size={12} />
                </ThemeIcon>
              ) : null}
              <span>{c.label}</span>
            </Group>
          </Anchor>
        );
      })}
    </MantineBreadcrumbs>
  );
}
